import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appOnlyDecimal]'
})
export class OnlyDecimalDirective {

  @Input() canApply: boolean = true;
  @Input() allowDecimals: boolean = true;

  private navigationKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'Clear',
    'Copy',
    'Paste'
  ];

  constructor(public el: ElementRef<HTMLInputElement>) { }

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (!this.canApply) { return; }

    const currentValue: string = this.el.nativeElement.value || '';

    if (
      this.navigationKeys.includes(e.key) ||
      (e.key === 'a' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'c' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'v' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'x' && (e.ctrlKey || e.metaKey))
    ) {
      return;
    }

    if (this.allowDecimals && (e.key === '.' || e.key === 'Decimal')) {
      if (currentValue.includes('.')) {
        e.preventDefault();
      }
      return;
    }

    const isDigit = /^[0-9]$/.test(e.key);

    if (!isDigit) {
      e.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (!this.canApply) { return; }

    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text/plain') || '';

    const sanitizedText = this.sanitizeDecimalInput(pastedText);
    if (sanitizedText) {
      document.execCommand('insertText', false, sanitizedText);
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    if (!this.canApply) { return; }

    event.preventDefault();
    const droppedText = event.dataTransfer?.getData('text') || '';

    const sanitizedText = this.sanitizeDecimalInput(droppedText);
    if (sanitizedText) {
      document.execCommand('insertText', false, sanitizedText);
    }
  }

  @HostListener('blur')
  onBlur() {
    if (!this.canApply) { return; }

    let value = this.el.nativeElement.value;

    if (value === '.' || value.endsWith('.')) {
      value = value.replace(/\./g, '');
      this.el.nativeElement.value = value;

      this.el.nativeElement.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    if (!this.canApply) { return; }

    const input = this.el.nativeElement;
    const sanitized = this.sanitizeDecimalInput(input.value);

    if (sanitized !== input.value) {
      const cursorPos = input.selectionStart ?? sanitized.length;
      input.value = sanitized;
      input.setSelectionRange(cursorPos - 1, cursorPos - 1);
      input.dispatchEvent(new Event('input'));
    }
  }

  private sanitizeDecimalInput(input: string): string {
    if (!input) return '';

    let clean = input.replace(/[^0-9.]/g, '');

    const parts = clean.split('.');
    if (parts.length > 2) {
      clean = parts[0] + '.' + parts.slice(1).join('');
    }

    if (!this.allowDecimals) {
      clean = clean.replace(/\./g, '');
    }

    return clean;
  }
}
